import * as React from "react";
import Header from "@amzn/awsui-components-react/polaris/header";
import Container from "@amzn/awsui-components-react/polaris/container";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Button from "@amzn/awsui-components-react/polaris/button";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Tabs from "@amzn/awsui-components-react/polaris/tabs";
import { API } from "aws-amplify";

// Types removed from client package — defined locally
interface Sigline {
  [key: string]: any;
}

interface CreatePillPackPrescriptionRequest {
  personId: string;
  spi?: string;
  prescriptions: any[];
}
import { MedicationOption } from "./PrescriptionDetailsBlock";
import { SiglineFormState } from "./SiglineBlock";
import PrescriptionTabContent, { PrescriptionEntry, EMPTY_PRESCRIPTION } from "./PrescriptionTabContent";

interface State {
  personId: string;
  spi: string;
  prescriptions: PrescriptionEntry[];
  activeTabIndex: number;
  medications: MedicationOption[];
  loadingMeds: boolean;
  submitting: boolean;
  results: any[] | null;
  error: string | null;
}

class AddPrescriptionV2 extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      personId: "",
      spi: "",
      prescriptions: [{ prescription: { ...EMPTY_PRESCRIPTION }, siglines: [] }],
      activeTabIndex: 0,
      medications: [],
      loadingMeds: true,
      submitting: false,
      results: null,
      error: null,
    };
  }

  componentDidMount() {
    this.loadMedications();
  }

  private loadMedications = async () => {
    try {
      const result = await API.get("ApaqPortalAPI", "/drug/get-drugs", {});
      const meds: MedicationOption[] = (result.medications || []).map(
        (m: any) => ({ drugId: m.drugId, description: m.description || m.drugId })
      );
      this.setState({ medications: meds, loadingMeds: false });
    } catch (e) {
      this.setState({ error: "Failed to load medications", loadingMeds: false });
    }
  };

  private updateEntry = (updated: PrescriptionEntry) => {
    this.setState((prev) => {
      const newPrescriptions = [...prev.prescriptions];
      newPrescriptions[prev.activeTabIndex] = updated;
      return { prescriptions: newPrescriptions };
    });
  };

  private addTab = () => {
    this.setState((prev) => ({
      prescriptions: [...prev.prescriptions, { prescription: { ...EMPTY_PRESCRIPTION }, siglines: [] }],
      activeTabIndex: prev.prescriptions.length,
    }));
  };

  private removeTab = (index: number) => {
    this.setState((prev) => {
      if (prev.prescriptions.length <= 1) return null;
      const newPrescriptions = prev.prescriptions.filter((_, i) => i !== index);
      const newActive = prev.activeTabIndex >= newPrescriptions.length
        ? newPrescriptions.length - 1
        : prev.activeTabIndex > index
          ? prev.activeTabIndex - 1
          : prev.activeTabIndex;
      return { prescriptions: newPrescriptions, activeTabIndex: newActive };
    });
  };

  private buildSiglinesForEntry = (siglines: SiglineFormState[]): Sigline[] | undefined => {
    if (siglines.length === 0) return undefined;
    return siglines.map((s) => {
      const sig: Sigline = {};
      if (s.text) sig.text = s.text;
      if (s.quantityPerDose) sig.quantityPerDose = parseFloat(s.quantityPerDose);
      if (s.quantityPerDay) sig.quantityPerDay = parseFloat(s.quantityPerDay);
      if (s.hoaCode) sig.hoaCode = s.hoaCode;
      if (s.hoaTimes.length > 0) sig.hoaTimes = s.hoaTimes;
      if (s.units) sig.units = s.units;
      if (s.scheduleType) sig.scheduleType = s.scheduleType as any;
      if (s.period) sig.period = parseInt(s.period, 10);
      if (s.scheduleType === "dow") sig.dow = s.dow;
      if (s.startDatetime) sig.startDatetime = s.startDatetime;
      if (s.endDatetime) sig.endDatetime = s.endDatetime;
      if (s.rawSigCode) sig.rawSigCode = s.rawSigCode;
      if (s.lineNumber) sig.lineNumber = parseInt(s.lineNumber, 10);
      if (s.version) sig.version = parseInt(s.version, 10);
      if (s.variableDosage) sig.variableDosage = s.variableDosage;
      if (s.variableFrequency) sig.variableFrequency = s.variableFrequency;
      if (s.minDosage) sig.minDosage = parseFloat(s.minDosage);
      if (s.maxDosage) sig.maxDosage = parseFloat(s.maxDosage);
      if (s.multiDosage) sig.multiDosage = s.multiDosage;
      if (s.frequency) sig.frequency = parseFloat(s.frequency);
      if (s.minFrequency) sig.minFrequency = parseFloat(s.minFrequency);
      if (s.maxFrequency) sig.maxFrequency = parseFloat(s.maxFrequency);
      if (s.alternatingPrescription) sig.alternatingPrescription = s.alternatingPrescription;
      if (s.prescriberDirectedHoa) sig.prescriberDirectedHoa = s.prescriberDirectedHoa.split(",").map((t) => t.trim());
      if (s.timesOfDay) sig.timesOfDay = s.timesOfDay.split(",").map((t) => t.trim());
      if (s.withMeals) sig.withMeals = s.withMeals.split(",").map((t) => t.trim());
      return sig;
    });
  };

  private handleSubmit = async () => {
    const { personId, spi, prescriptions } = this.state;
    if (!personId) {
      this.setState({ error: "Person ID is required" });
      return;
    }
    const missingDrug = prescriptions.findIndex((e) => !e.prescription.drugId);
    if (missingDrug !== -1) {
      this.setState({ error: `Prescription ${missingDrug + 1} is missing a Drug ID`, activeTabIndex: missingDrug });
      return;
    }
    this.setState({ submitting: true, error: null, results: null });
    try {
      const builtPrescriptions = prescriptions.map((entry) => {
        const { drugIdInput, ...rx } = entry.prescription;
        const cleaned: any = { drugId: rx.drugId };
        if (rx.initialRefills) cleaned.initialRefills = rx.initialRefills;
        if (rx.quantityAuthorized) cleaned.quantityAuthorized = rx.quantityAuthorized;
        if (rx.daysSupply) cleaned.daysSupply = rx.daysSupply;
        if (rx.dispenseQuantity) cleaned.dispenseQuantity = rx.dispenseQuantity;
        if (rx.dispenseDaysSupply) cleaned.dispenseDaysSupply = rx.dispenseDaysSupply;
        if (rx.expirationDate) cleaned.expirationDate = rx.expirationDate;
        if (rx.oneTimeFillReason) cleaned.oneTimeFillReason = rx.oneTimeFillReason;
        if (rx.status) cleaned.status = rx.status;
        if (rx.inPackets) cleaned.inPackets = rx.inPackets;
        const siglines = this.buildSiglinesForEntry(entry.siglines);
        if (siglines) cleaned.siglines = siglines;
        return cleaned;
      });

      const request: CreatePillPackPrescriptionRequest = {
        personId,
        spi: spi || undefined,
        prescriptions: builtPrescriptions,
      };
      const response = await API.post("ApaqPortalAPI", "/prescription/pillpack/create", { body: request });
      const results = (response.results || []).map((r: any, i: number) => {
        const entry = prescriptions[i];
        const label = entry
          ? (entry.prescription.drugIdInput || entry.prescription.drugId || `Prescription ${i + 1}`)
          : `Prescription ${i + 1}`;
        return { ...r, label };
      });
      this.setState({ results, submitting: false });
    } catch (e) {
      const err = e as any;
      const message =
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : "Request failed");
      this.setState({ error: message, submitting: false });
    }
  };

  render() {
    const { personId, spi, prescriptions, activeTabIndex, medications, loadingMeds, error, submitting, results } = this.state;

    if (loadingMeds) return <Spinner size="large" />;

    return (
      <div>
        <Header variant="h2">Add Prescription via PPCore</Header>

        <SpaceBetween size="l">
          {error && (
            <Flashbar items={[{ type: "error", content: error, dismissible: true, onDismiss: () => this.setState({ error: null }) }]} />
          )}
          {results && (
            <Flashbar items={results.map((r) => ({
              type: r.started ? "success" as const : "error" as const,
              content: `${r.label}: ${r.started ? `Started (${r.executionName || ''})` : r.errorMessage || "Failed"}`,
            }))} />
          )}

          <Flashbar items={[{ type: "info", content: "Adding a prescription is an asynchronous process. Submitting will kick off a background workflow — results may not be immediate." }]} />

          <Container header={<Header variant="h3">User Details</Header>}>
            <ColumnLayout columns={2}>
              <FormField label="Person ID" description="Required">
                <Input value={personId} onChange={({ detail }) => this.setState({ personId: detail.value })} placeholder="amzn1.account...." />
              </FormField>
              <FormField label="SPI" description="Optional. Server resolves a default if not provided.">
                <Input value={spi} onChange={({ detail }) => this.setState({ spi: detail.value })} placeholder="6633950178005" />
              </FormField>
            </ColumnLayout>
          </Container>

          <Tabs
            activeTabId={String(activeTabIndex)}
            onChange={({ detail }) => this.setState({ activeTabIndex: Number(detail.activeTabId) })}
            tabs={prescriptions.map((entry, i) => ({
              id: String(i),
              label: (entry.prescription.drugIdInput || `Prescription ${i + 1}`).replace(/\s*\(.*\)$/, ''),
              dismissible: prescriptions.length > 1,
              dismissLabel: `Remove Prescription ${i + 1}`,
              onDismiss: () => this.removeTab(i),
            }))}
            actions={<Button iconName="add-plus" variant="icon" onClick={this.addTab} />}
          />

          <PrescriptionTabContent
            entry={prescriptions[activeTabIndex]}
            medications={medications}
            onChange={this.updateEntry}
          />

          <Button variant="primary" disabled={!personId || !prescriptions[activeTabIndex].prescription.drugId} loading={submitting} onClick={this.handleSubmit}>
            Submit
          </Button>
        </SpaceBetween>
      </div>
    );
  }
}

export default AddPrescriptionV2;
